import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Calendar, Check, ChevronDown, Search, Send, X } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { useWebsitesStore } from "../../store/websitesStore";
import { decodeTokenService } from "../../services/decodeTokenService";
import { getAbandonedCarts } from "../../services/funnels";
import { recoverAbandonedCart } from "../../services/whatsapp/whatsappService";
import { handleApi, logErrorToSentry } from "../../utils/handleApi";
import { dictionary, Language } from "../../utils/dictionary";
import { getStartDate, Period, PERIODS } from "../../utils/const";
import { formatDateInZone } from "../../utils/formatters";
import PeriodSelect from "../../pages/AnaliticsPage/PeriodSelect";
import Loader from "../common/Loader";
import { DefaultButton } from "../buttons/DefaultButton";

interface CustomerData {
  name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address?: string | null;
  address_detail?: string | null;
  city?: string | null;
  department?: string | null;
}

interface AbandonedCartProduct {
  name: string;
  images: string[];
  units: number;
  unit_price: number;
  unit_price_with_discount: number;
  total_value_with_discount: number;
  product_id: string;
}

interface AbandonedCart {
  session_id: string;
  website_id: string;
  last_stage: string;
  customer_data: CustomerData;
  first_seen_at: string;
  last_seen_at: string;
  products?: AbandonedCartProduct[];
}

interface Pagination {
  page: number;
  size: number;
  total_items: number;
  total_pages: number;
}

interface AbandonedCartsResponse {
  carts: AbandonedCart[];
  pagination: Pagination;
}

const QUERY_KEYS = {
  date: "abandoned_date",
  page: "abandoned_page",
  size: "abandoned_size",
  search: "abandoned_search",
  stage: "abandoned_stage",
  websiteId: "abandoned_website_id",
};

const PAGE_SIZE_OPTIONS = [15, 30, 50];
const SHOW_RECOVER_CART_BUTTON = false;

const LAST_STAGE_LABEL_KEY: Record<string, string> = {
  pageview: "funnel-stage-pageview",
  addToCart: "funnel-stage-addToCart",
  initiateCheckout: "funnel-stage-initiateCheckout",
  addCheckoutInfo: "abandoned-stage-checkoutForm",
  addPaymentInfo: "abandoned-stage-paymentModal",
};

const parsePageParam = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseSizeParam = (value: string | null) => {
  const parsed = Number(value);
  return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : PAGE_SIZE_OPTIONS[0];
};

const getVisiblePaginationPages = (currentPage: number, totalPages: number) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const pages = new Set([0, totalPages - 1, currentPage]);
  if (currentPage > 0) pages.add(currentPage - 1);
  if (currentPage < totalPages - 1) pages.add(currentPage + 1);

  const sortedPages = Array.from(pages).sort((a, b) => a - b);
  return sortedPages.reduce<(number | "ellipsis")[]>((items, page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      items.push("ellipsis");
    }
    items.push(page);
    return items;
  }, []);
};

const isPeriod = (value: string | null): value is Period =>
  Boolean(value && PERIODS.some((period) => period.key === value));

const emptyPagination = (page: number, size: number): Pagination => ({
  page,
  size,
  total_items: 0,
  total_pages: 0,
});

interface FilterOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  className?: string;
  icon?: ReactNode;
}

const FilterSelect = ({ value, options, onChange, className = "", icon }: FilterSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className={`relative shrink-0 ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className={`flex h-12 w-full items-center gap-2 rounded-xl bg-white px-4 text-sm font-medium transition-colors hover:bg-gray-50 ${
          isOpen ? "border border-[#4368E5] text-black" : "border border-[#C8C8C8] text-gray-500"
        }`}
      >
        {icon ? <span className="flex shrink-0 text-gray-400">{icon}</span> : null}
        <span className="flex-1 truncate text-left">{selectedOption?.label}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 transition-transform ${isOpen ? "rotate-180 text-gray-700" : "text-gray-400"}`}
        />
      </button>
      {isOpen ? (
        <div className="absolute left-0 top-full z-50 mt-2 max-h-72 w-full min-w-[12rem] overflow-auto rounded-2xl border border-gray-200 bg-white py-2 shadow-xl">
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value || "all"}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors ${
                  isSelected ? "bg-[#E8EAFF] text-[#4D5FE8]" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Check size={14} className={isSelected ? "opacity-100" : "opacity-0"} />
                <span className="truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const AbandonedCartsTable = () => {
  const { language, timezone } = useAuth();
  const { websites, initialize } = useWebsitesStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPeriodSelect, setShowPeriodSelect] = useState(false);
  const [carts, setCarts] = useState<AbandonedCart[]>([]);
  const [pagination, setPagination] = useState<Pagination>(emptyPagination(0, PAGE_SIZE_OPTIONS[0]));
  const [isLoading, setIsLoading] = useState(false);
  const [recoveringId, setRecoveringId] = useState<string | null>(null);
  const [recoveredIds, setRecoveredIds] = useState<Set<string>>(new Set());

  const dateParam = searchParams.get(QUERY_KEYS.date);
  const selectedDate = isPeriod(dateParam) ? dateParam : Period.Last30Days;
  const selectedPage = parsePageParam(searchParams.get(QUERY_KEYS.page), 0);
  const selectedSize = parseSizeParam(searchParams.get(QUERY_KEYS.size));
  const selectedSearch = searchParams.get(QUERY_KEYS.search) || "";
  const selectedStage = searchParams.get(QUERY_KEYS.stage) || "";
  const selectedWebsiteId = searchParams.get(QUERY_KEYS.websiteId) || "";
  const [searchInput, setSearchInput] = useState(selectedSearch);

  useEffect(() => {
    setSearchInput(selectedSearch);
  }, [selectedSearch]);

  useEffect(() => {
    if (websites.length === 0) initialize();
  }, [initialize, websites.length]);

  const selectedPeriodLabel = dictionary[selectedDate]?.[language] ?? selectedDate;

  const stageOptions = useMemo(
    () => [
      {
        value: "",
        label: dictionary["abandoned-carts-all-stages"]?.[language] || "Todas las etapas",
      },
      {
        value: "addCheckoutInfo",
        label:
          dictionary[LAST_STAGE_LABEL_KEY.addCheckoutInfo]?.[language as Language] ||
          "Formulario checkout",
      },
      {
        value: "addPaymentInfo",
        label:
          dictionary[LAST_STAGE_LABEL_KEY.addPaymentInfo]?.[language as Language] ||
          "Modal de pago",
      },
    ],
    [language]
  );
  const selectedStageValue = stageOptions.some((option) => option.value === selectedStage)
    ? selectedStage
    : "";

  const funnelOptions = useMemo(
    () => [
      {
        value: "",
        label: dictionary["abandoned-carts-all-funnels"]?.[language] || "Todos los embudos",
      },
      ...websites.map((website) => ({
        value: website.websiteId,
        label: website.name || website.productName || website.websiteId,
      })),
    ],
    [language, websites]
  );
  const selectedFunnelLabel =
    funnelOptions.find((option) => option.value === selectedWebsiteId)?.label || selectedWebsiteId;
  const selectedStageLabel =
    stageOptions.find((option) => option.value === selectedStageValue)?.label || selectedStageValue;

  const updateParams = (updates: Record<string, string | number | null>, resetPage = true) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "") {
        nextParams.delete(key);
        return;
      }
      nextParams.set(key, String(value));
    });

    if (resetPage) {
      nextParams.set(QUERY_KEYS.page, "0");
    }

    setSearchParams(nextParams);
  };

  useEffect(() => {
    if (!selectedStage || selectedStageValue) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(QUERY_KEYS.stage);
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, selectedStage, selectedStageValue, setSearchParams]);

  const clearFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    [
      QUERY_KEYS.search,
      QUERY_KEYS.stage,
      QUERY_KEYS.websiteId,
      QUERY_KEYS.date,
      QUERY_KEYS.page,
      QUERY_KEYS.size,
    ].forEach((key) => nextParams.delete(key));
    setSearchParams(nextParams);
  };

  const getStageLabel = (stage: string) => {
    const key = LAST_STAGE_LABEL_KEY[stage];
    return key ? dictionary[key]?.[language as Language] ?? stage : stage;
  };

  const getFunnelName = (websiteId: string) => {
    const site = websites.find((w) => w.websiteId === websiteId);
    return site?.name || site?.productName || "—";
  };

  const handleRecover = async (cart: AbandonedCart, funnelName: string) => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId || !cart.customer_data.phone) return;

    setRecoveringId(cart.session_id);
    const { res, error } = await handleApi(
      recoverAbandonedCart({
        owner_id: ownerId,
        phone: cart.customer_data.phone,
        customer_name:
          [cart.customer_data.name, cart.customer_data.last_name].filter(Boolean).join(" ") ||
          null,
        funnel_name: funnelName,
        products: (cart.products || []).map((p) => ({
          name: p.name,
          units: p.units,
          total_value_with_discount: p.total_value_with_discount,
        })),
      }),
      false
    );
    setRecoveringId(null);

    if (res) {
      setRecoveredIds((prev) => {
        const next = new Set(prev);
        next.add(cart.session_id);
        return next;
      });
      toast.success(
        dictionary["recover-cart-success"]?.[language] ||
          "Mensaje de recuperacion enviado por WhatsApp"
      );
    } else if (error) {
      const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
      const detailRecord =
        detail && typeof detail === "object" ? (detail as Record<string, unknown>) : null;

      const trigger =
        typeof detailRecord?.trigger === "string"
          ? detailRecord.trigger
          : null;
      const objMessage =
        typeof detailRecord?.message === "string"
          ? detailRecord.message
          : null;
      const stringDetail = typeof detail === "string" ? detail : null;

      let message: string;
      if (trigger === "wa_token_expired") {
        message =
          dictionary["recover-cart-token-expired"]?.[language] ||
          "El token de WhatsApp expiro. Reconecta la cuenta desde Configuracion -> WhatsApp -> Gestionar API.";
      } else if (trigger === "wa_outside_24h_window") {
        message =
          dictionary["recover-cart-outside-window"]?.[language] ||
          "Pasaron mas de 24h del ultimo mensaje del cliente. Solo se pueden enviar plantillas aprobadas.";
      } else if (stringDetail && stringDetail.toLowerCase().includes("plantilla")) {
        message = dictionary["recover-cart-no-template"]?.[language] || stringDetail;
      } else {
        message =
          objMessage ||
          stringDetail ||
          dictionary["recover-cart-error"]?.[language] ||
          "No se pudo enviar el mensaje de recuperacion";
      }

      toast.error(message);
    }
  };

  useEffect(() => {
    const owner_id = decodeTokenService.getUserIdFromToken();
    if (!owner_id) return;

    const period = PERIODS.find((p) => p.key === selectedDate);
    const payload = {
      owner_id,
      website_id: selectedWebsiteId,
      created_at: getStartDate(new Date(), period?.value || 30),
      page: selectedPage,
      size: selectedSize,
      ...(selectedSearch ? { search: selectedSearch } : {}),
      ...(selectedStageValue ? { stage: selectedStageValue } : {}),
    };

    let isActive = true;
    setIsLoading(true);
    getAbandonedCarts(payload)
      .then((res: AbandonedCartsResponse | AbandonedCart[]) => {
        if (!isActive) return;

        if (Array.isArray(res)) {
          setCarts(res);
          setPagination({
            page: 0,
            size: selectedSize,
            total_items: res.length,
            total_pages: res.length > 0 ? 1 : 0,
          });
          return;
        }

        setCarts(Array.isArray(res?.carts) ? res.carts : []);
        setPagination(res?.pagination || emptyPagination(selectedPage, selectedSize));
      })
      .catch((err) => {
        if (!isActive) return;
        logErrorToSentry(err);
        setCarts([]);
        setPagination(emptyPagination(selectedPage, selectedSize));
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedDate, selectedPage, selectedSearch, selectedSize, selectedStageValue, selectedWebsiteId]);

  const headers = [
    dictionary["customer-name"]?.[language] || "Nombre cliente",
    "Email",
    dictionary["phone-label"]?.[language] || "Telefono",
    dictionary["funnel"]?.[language] || "Embudo",
    dictionary["last-stage-reached"]?.[language] || "Ultima etapa",
    dictionary["last-activity"]?.[language] || "Ultima actividad",
    "",
    "",
  ];

  const hasActiveFilters = Boolean(
    selectedSearch || selectedStageValue || selectedWebsiteId || selectedDate !== Period.Last30Days
  );
  const visiblePages = getVisiblePaginationPages(pagination.page, pagination.total_pages);

  return (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 px-4 pt-4">
        <p className="text-sm text-gray-500 max-w-lg">
          {dictionary["abandoned-carts-description"]?.[language]}
        </p>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-medium text-gray-600 transition-colors hover:border-fluxi hover:text-fluxi"
          >
            <X size={14} />
            {dictionary["clear"]?.[language] || "Limpiar"}
          </button>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 px-4">
        <div className="relative w-full sm:w-56">
          <button
            type="button"
            onClick={() => setShowPeriodSelect(!showPeriodSelect)}
            className={`flex h-12 w-full items-center gap-2 rounded-xl bg-white px-4 text-sm font-medium transition-colors hover:bg-gray-50 ${
              showPeriodSelect ? "border border-[#4368E5] text-black" : "border border-[#C8C8C8] text-gray-500"
            }`}
          >
            <Calendar className={`h-5 w-5 ${showPeriodSelect ? "text-gray-700" : "text-gray-400"}`} />
            <span className="flex-1 truncate text-left">{selectedPeriodLabel}</span>
            <ChevronDown
              size={18}
              className={`shrink-0 transition-transform ${
                showPeriodSelect ? "rotate-180 text-gray-700" : "text-gray-400"
              }`}
            />
          </button>
          {showPeriodSelect && (
            <PeriodSelect
              selected={selectedDate}
              onSelect={(period) => {
                updateParams({ [QUERY_KEYS.date]: period });
                setShowPeriodSelect(false);
              }}
              onClose={() => setShowPeriodSelect(false)}
            />
          )}
        </div>

        <FilterSelect
          value={selectedWebsiteId}
          options={funnelOptions}
          onChange={(value) => updateParams({ [QUERY_KEYS.websiteId]: value })}
          className="w-full sm:w-56"
        />

        <FilterSelect
          value={selectedStageValue}
          options={stageOptions}
          onChange={(value) => updateParams({ [QUERY_KEYS.stage]: value })}
          className="w-full sm:w-56"
        />

        <div className="relative min-w-[240px] flex-1 xl:max-w-md">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                updateParams({ [QUERY_KEYS.search]: searchInput.trim() });
              }
            }}
            placeholder={
              dictionary["abandoned-carts-search-placeholder"]?.[language] ||
              "Buscar nombre, email o celular"
            }
            className="h-12 w-full rounded-xl border border-[#C8C8C8] bg-white pl-11 pr-10 text-sm font-medium text-gray-700 outline-none transition-colors placeholder:text-gray-400 hover:bg-gray-50 focus:border-[#4368E5]"
          />
          {searchInput ? (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                updateParams({ [QUERY_KEYS.search]: null });
              }}
              className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Limpiar busqueda"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>

        <DefaultButton
          title={dictionary["search"]?.[language] || "Buscar"}
          onClick={() => updateParams({ [QUERY_KEYS.search]: searchInput.trim() })}
          showIcon={false}
          className="h-12 px-8"
        />
      </div>

      {hasActiveFilters ? (
        <div className="mt-4 flex flex-wrap gap-2 px-4">
          {selectedDate !== Period.Last30Days ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8EAFF] text-[#4D5FE8] rounded-full text-sm font-medium">
              <span>{selectedPeriodLabel}</span>
              <button
                type="button"
                onClick={() => updateParams({ [QUERY_KEYS.date]: null })}
                className="hover:bg-[#4D5FE8] hover:text-white rounded-full p-0.5 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
          {selectedWebsiteId ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E2FBE8] text-[#3C7E45] rounded-full text-sm font-medium max-w-[250px]">
              <span className="truncate">{selectedFunnelLabel}</span>
              <button
                type="button"
                onClick={() => updateParams({ [QUERY_KEYS.websiteId]: null })}
                className="hover:bg-[#3C7E45] hover:text-white rounded-full p-0.5 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
          {selectedStageValue ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 rounded-full text-sm font-medium">
              <span>{selectedStageLabel}</span>
              <button
                type="button"
                onClick={() => updateParams({ [QUERY_KEYS.stage]: null })}
                className="hover:bg-orange-700 hover:text-white rounded-full p-0.5 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
          {selectedSearch ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium max-w-[250px]">
              <span className="truncate">{selectedSearch}</span>
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  updateParams({ [QUERY_KEYS.search]: null });
                }}
                className="hover:bg-gray-700 hover:text-white rounded-full p-0.5 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Loader />
        </div>
      ) : carts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm">{dictionary["no-abandoned-carts"]?.[language]}</p>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full" style={{ minWidth: "920px" }}>
            <thead className="bg-gray-50">
              <tr>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {carts.map((cart) => {
                const funnelName = getFunnelName(cart.website_id);
                const hasPhone = Boolean(cart.customer_data.phone);
                const canShowRecoverButton = SHOW_RECOVER_CART_BUTTON && hasPhone;
                const isRecovering = recoveringId === cart.session_id;
                const wasRecovered = recoveredIds.has(cart.session_id);
                return (
                  <tr key={cart.session_id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-900 max-w-[170px] truncate">
                      {[cart.customer_data.name, cart.customer_data.last_name].filter(Boolean).join(" ") || (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-blue-500 max-w-[210px] truncate">
                      {cart.customer_data.email || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {cart.customer_data.phone || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 max-w-[180px] truncate">
                      {funnelName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
                        {getStageLabel(cart.last_stage)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDateInZone(cart.last_seen_at, timezone)}
                    </td>
                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                      {canShowRecoverButton ? (
                        <button
                          type="button"
                          onClick={() => handleRecover(cart, funnelName)}
                          disabled={isRecovering || wasRecovered}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            wasRecovered
                              ? "bg-green-50 text-green-700 cursor-default"
                              : "bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed"
                          }`}
                        >
                          <Send size={12} />
                          {wasRecovered
                            ? dictionary["recover-cart-sent-badge"]?.[language] || "Enviado"
                            : isRecovering
                              ? dictionary["recover-cart-sending"]?.[language] || "Enviando..."
                              : dictionary["recover-cart-button"]?.[language] || "Recuperar compra"}
                        </button>
                      ) : null}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <Link
                        to={`/abandoned-cart/${cart.session_id}`}
                        state={{ cart, funnelName }}
                        className="text-blue-500 whitespace-nowrap text-xs hover:underline"
                      >
                        {dictionary["view-details"]?.[language] || "Ver detalles"}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-10 py-7 border-t border-gray-100 bg-[#F7F8FC]">
        <div className="text-sm text-gray-500">
          {pagination.total_items === 0
            ? "Mostrando 0 - 0 de 0"
            : `Mostrando ${pagination.page * pagination.size + 1} - ${Math.min(
                (pagination.page + 1) * pagination.size,
                pagination.total_items
              )} de ${pagination.total_items}`}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => updateParams({ [QUERY_KEYS.page]: pagination.page - 1 }, false)}
            disabled={pagination.page <= 0}
            className="h-10 px-4 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-600 transition-colors hover:border-fluxi hover:text-fluxi disabled:bg-white disabled:text-gray-300 disabled:hover:border-gray-200"
          >
            {dictionary["previous"]?.[language] || "Anterior"}
          </button>
          {visiblePages.map((page, index) =>
            page === "ellipsis" ? (
              <span
                key={`ellipsis-${index}`}
                className="h-10 min-w-8 inline-flex items-center justify-center text-sm text-gray-500"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                type="button"
                onClick={() => updateParams({ [QUERY_KEYS.page]: page }, false)}
                className={`h-10 min-w-10 rounded-md border text-sm font-medium transition-colors ${
                  page === pagination.page
                    ? "border-fluxi bg-fluxi text-white shadow-sm"
                    : "border-gray-200 bg-white text-gray-700 hover:border-fluxi hover:text-fluxi"
                }`}
              >
                {page + 1}
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => updateParams({ [QUERY_KEYS.page]: pagination.page + 1 }, false)}
            disabled={pagination.page + 1 >= pagination.total_pages}
            className="h-10 px-4 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:border-fluxi hover:text-fluxi disabled:bg-white disabled:text-gray-300 disabled:hover:border-gray-200"
          >
            {dictionary["next"]?.[language] || "Siguiente"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AbandonedCartsTable;
